/* ica-hero-video.js — B5 Fix 4: hero video mobile-weight guard.

   The homepage hero MP4 is ~15MB. Shipping its <source> in the markup makes
   Chrome fetch the file on EVERY viewport — even when the <video> is CSS-hidden
   on mobile — which tanks mobile Lighthouse (was 66). So the section renders the
   <video> WITHOUT a <source> and carries the chosen URL/type on data-attributes.

   This module injects the <source> + starts playback ONLY when:
     - matchMedia('(min-width:1024px)') matches (true desktop), AND
     - the user has NOT asked to reduce data (prefers-reduced-data: reduce), AND
     - the user has NOT asked to reduce motion (prefers-reduced-motion: reduce).

   Result: mobile / Slow-4G / data-saver / reduced-motion never even request the
   MP4 — only the responsive LCP poster <img> ships there. On desktop the clip
   loads after the rest of the page and fades in over the (already-painted) poster,
   so it never becomes the LCP element and causes no layout shift (the <video> is
   absolutely positioned over the <img>, which reserves the box).

   We also listen for viewport changes (a desktop tab resized up from mobile, or an
   orientation flip on a large tablet) so the clip can lazily upgrade in — but we
   never tear it down once loaded (no point re-downloading), and we never load it
   on a connection the visitor flagged as constrained.

   Vanilla JS, no jQuery. Fail-silent: any error leaves the poster in place. */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 1024px)';

  function prefersReducedData() {
    try {
      // Honour data-saver. Spec is still shipping; treat only an explicit
      // "reduce" match as opt-out so unsupported browsers fall through to the
      // desktop check (where the clip is wanted).
      return window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)').matches;
    } catch (e) { return false; }
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { return false; }
  }

  function isDesktop() {
    try {
      return window.matchMedia && window.matchMedia(DESKTOP_QUERY).matches;
    } catch (e) {
      // No matchMedia (very old browser): be conservative, skip the heavy clip.
      return false;
    }
  }

  function shouldLoad() {
    return isDesktop() && !prefersReducedData() && !prefersReducedMotion();
  }

  function loadVideo(video) {
    if (video.__icaHeroLoaded) return;
    var src = video.getAttribute('data-video-src');
    if (!src) return;
    video.__icaHeroLoaded = true;

    var source = document.createElement('source');
    source.setAttribute('src', src);
    source.setAttribute('type', video.getAttribute('data-video-type') || 'video/mp4');
    video.appendChild(source);

    // Required for inline autoplay across browsers.
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('preload', 'auto');

    try { video.load(); } catch (e) { /* ignore */ }

    var play = function () {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        // Autoplay rejection (rare for muted inline video) is non-fatal — the
        // poster simply stays. Don't surface anything to the user.
        p.catch(function () {});
      }
    };

    // Play once there's enough buffered to avoid a flash of an empty frame.
    if (video.readyState >= 2) {
      play();
    } else {
      video.addEventListener('canplay', play, { once: true });
      // Backstop in case canplay never fires (cached file etc.).
      video.addEventListener('loadeddata', play, { once: true });
    }

    video.classList.add('ica-hero-video--ready');
  }

  function setup(video) {
    if (!video || video.__icaHeroInit) return;
    video.__icaHeroInit = true;

    var attempt = function () {
      if (shouldLoad()) loadVideo(video);
    };

    attempt();

    // If we started on mobile but the viewport grows to desktop (resize /
    // orientation change), lazily upgrade in. addEventListener('change') is the
    // modern MQL API; addListener is the deprecated fallback for older Safari.
    if (!video.__icaHeroLoaded && window.matchMedia) {
      try {
        var mql = window.matchMedia(DESKTOP_QUERY);
        var onChange = function () { attempt(); };
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', onChange);
        } else if (typeof mql.addListener === 'function') {
          mql.addListener(onChange);
        }
      } catch (e) { /* fail silent */ }
    }
  }

  function init() {
    var videos = document.querySelectorAll('[data-ica-hero-video]');
    Array.prototype.forEach.call(videos, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
